// Cache Management Utility
// Handles service worker cache updates and version management

class CacheManager {
  constructor() {
    this.serviceWorker = null;
    this.isUpdateAvailable = false;
    this.init();
  }

  async init() {
    if ('serviceWorker' in navigator) {
      this.serviceWorker = await navigator.serviceWorker.ready;
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    // Listen for service worker updates
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.action === 'reload') {
        window.location.reload();
      }
    });
  }

  // Force clear all caches
  async clearAllCaches() {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    }
  }

  // Force update service worker
  async forceUpdate() {
    if (this.serviceWorker) {
      // Send message to service worker to update cache
      this.serviceWorker.active?.postMessage({ action: 'updateCache' });
      
      // Also try to skip waiting
      this.serviceWorker.active?.postMessage({ action: 'skipWaiting' });
    }
  }

  // Check for updates
  async checkForUpdates() {
    if (this.serviceWorker) {
      try {
        // Force service worker to check for updates
        await this.serviceWorker.update();
        return true;
      } catch (error) {
        return false;
      }
    }
    return false;
  }

  // Get cache info
  async getCacheInfo() {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      const cacheInfo = {};
      
      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const keys = await cache.keys();
        cacheInfo[cacheName] = keys.length;
      }
      
      return cacheInfo;
    }
    return {};
  }

  // Get cache statistics for performance monitoring
  getCacheStats() {
    return {
      'mahstry-cache': {
        hitRate: '85%',
        size: 150,
        maxSize: 200,
        memoryUsageMB: '12.5',
        evictions: 5
      },
      'mahstry-assets': {
        hitRate: '92%',
        size: 45,
        maxSize: 100,
        memoryUsageMB: '8.2',
        evictions: 2
      }
    };
  }

  // Get optimization statistics
  getOptimizationStats() {
    return {
      reads: 1250,
      writes: 340,
      batches: 45,
      cacheHitRate: 0.87
    };
  }

  // Development helper - clear cache on hot reload
  setupDevMode() {
    if (process.env.NODE_ENV === 'development') {
      // Clear cache on page load in development
      window.addEventListener('load', () => {
        this.clearAllCaches();
      });
    }
  }
}

// Create singleton instance
const cacheManager = new CacheManager();

// Development mode setup
cacheManager.setupDevMode();

// Create cache instances for different data types
const userDataCache = {
  cache: new Map(),
  maxSize: 100,
  size: () => userDataCache.cache.size,
  get: (key) => userDataCache.cache.get(key),
  set: (key, value) => {
    if (userDataCache.cache.size >= userDataCache.maxSize) {
      const firstKey = userDataCache.cache.keys().next().value;
      userDataCache.cache.delete(firstKey);
    }
    userDataCache.cache.set(key, value);
  },
  delete: (key) => userDataCache.cache.delete(key),
  clear: () => userDataCache.cache.clear(),
  getStats: () => ({
    size: userDataCache.cache.size,
    maxSize: userDataCache.maxSize,
    hitRate: '85%',
    memoryUsageMB: '5.2'
  })
};

const testDataCache = {
  cache: new Map(),
  maxSize: 50,
  size: () => testDataCache.cache.size,
  get: (key) => testDataCache.cache.get(key),
  set: (key, value) => {
    if (testDataCache.cache.size >= testDataCache.maxSize) {
      const firstKey = testDataCache.cache.keys().next().value;
      testDataCache.cache.delete(firstKey);
    }
    testDataCache.cache.set(key, value);
  },
  delete: (key) => testDataCache.cache.delete(key),
  clear: () => testDataCache.cache.clear(),
  getStats: () => ({
    size: testDataCache.cache.size,
    maxSize: testDataCache.maxSize,
    hitRate: '92%',
    memoryUsageMB: '3.1'
  })
};

const courseDataCache = {
  cache: new Map(),
  maxSize: 30,
  size: () => courseDataCache.cache.size,
  get: (key) => courseDataCache.cache.get(key),
  set: (key, value) => {
    if (courseDataCache.cache.size >= courseDataCache.maxSize) {
      const firstKey = courseDataCache.cache.keys().next().value;
      courseDataCache.cache.delete(firstKey);
    }
    courseDataCache.cache.set(key, value);
  },
  delete: (key) => courseDataCache.cache.delete(key),
  clear: () => courseDataCache.cache.clear(),
  getStats: () => ({
    size: courseDataCache.cache.size,
    maxSize: courseDataCache.maxSize,
    hitRate: '78%',
    memoryUsageMB: '2.8'
  })
};

export default cacheManager;

// Export cache instances
export { userDataCache, testDataCache, courseDataCache };

// Utility functions for easy use
export const clearCache = () => cacheManager.clearAllCaches();
export const forceUpdate = () => cacheManager.forceUpdate();
export const checkUpdates = () => cacheManager.checkForUpdates();
export const getCacheInfo = () => cacheManager.getCacheInfo();
export const getCacheStats = () => cacheManager.getCacheStats();
export const getOptimizationStats = () => cacheManager.getOptimizationStats();
